import { Box, MenuItem, Select, Typography, Button } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';

const periodOptions = [
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];

const PeriodFilterDash = ({
  showPeriodFilter = true,
  showMonthFilter = true,
  showYearFilter = true,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  refetchDashboard,
  listYears = [],
  setSelectedYear,
  setSelectedMonth,
  selectedYear,
  selectedMonth,
}) => {
  // Initialize state based on props
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [customStartDate, setCustomStartDate] = useState(startDate ? dayjs(startDate) : null);
  const [customEndDate, setCustomEndDate] = useState(endDate ? dayjs(endDate) : null);
  const [tempSelectedYear, setTempSelectedYear] = useState(selectedYear ? String(selectedYear) : '');
  const [tempSelectedMonth, setTempSelectedMonth] = useState(
    selectedMonth ? selectedMonth - 1 : null
  );

  // Sync state with props
  useEffect(() => {
    // Update year
    if (selectedYear && listYears.includes(Number(selectedYear))) {
      setTempSelectedYear(String(selectedYear));
    } else if (selectedYear) {
      console.warn(`selectedYear ${selectedYear} is not in listYears`, listYears);
      setTempSelectedYear('');
    }

    // Update month
    setTempSelectedMonth(selectedMonth ? selectedMonth - 1 : null);

    // Update dates
    setCustomStartDate(startDate ? dayjs(startDate) : null);
    setCustomEndDate(endDate ? dayjs(endDate) : null);

    // Derive selectedPeriod
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const daysDiff = end.diff(start, 'day');

      if (end.isSame(dayjs(), 'day')) {
        if (daysDiff === 30) {
          setSelectedPeriod('30');
        } else if (daysDiff === 60) {
          setSelectedPeriod('60');
        } else if (daysDiff === 90) {
          setSelectedPeriod('90');
        } else {
          setSelectedPeriod('');
        }
      } else {
        setSelectedPeriod('');
      }
    }
  }, [selectedYear, selectedMonth, startDate, endDate, listYears]);

  const handleYearChange = (event) => {
    const year = event.target.value;
    setTempSelectedYear(year);
  };

  const handleMonthChange = (newValue) => {
    setTempSelectedMonth(newValue ? newValue.month() : null);
  };

  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);

    let newStartDate, newEndDate;
    if (period) {
      const days = parseInt(period, 10);
      newStartDate = dayjs().subtract(days, 'day');
      newEndDate = dayjs();
    }

    setCustomStartDate(newStartDate);
    setCustomEndDate(newEndDate);
    setTempSelectedYear(''); // Reset year when selecting a period
    setTempSelectedMonth(null); // Reset month when selecting a period
  };

  const handleCustomDateChange = () => {
    let start, end;
    if (selectedPeriod) {
      start = customStartDate ? customStartDate.format('YYYY-MM-DD') : null;
      end = customEndDate ? customEndDate.format('YYYY-MM-DD') : null;
    } else {
      start = tempSelectedYear && tempSelectedMonth !== null
        ? dayjs()
            .year(Number(tempSelectedYear))
            .month(tempSelectedMonth)
            .startOf('month')
            .format('YYYY-MM-DD')
        : customStartDate
        ? customStartDate.format('YYYY-MM-DD')
        : null;
      end = tempSelectedYear && tempSelectedMonth !== null
        ? dayjs()
            .year(Number(tempSelectedYear))
            .month(tempSelectedMonth)
            .endOf('month')
            .format('YYYY-MM-DD')
        : customEndDate
        ? customEndDate.format('YYYY-MM-DD')
        : null;
    }

    if (start && end) {
      onStartDateChange(start);
      onEndDateChange(end);
    }
    if (tempSelectedYear) {
      setSelectedYear(Number(tempSelectedYear));
    }
    if (tempSelectedMonth !== null) {
      setSelectedMonth(tempSelectedMonth + 1);
    }
    refetchDashboard();
  };

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'end',
        width: '100%',
        gap: 2,
        p: '8px',
        '.MuiInputBase-input': { padding: '5px', color: '#000' },
        '.MuiButtonBase-root': { color: '#000' },
        '.MuiTypography-root': { color: '#000' },
      })}
    >
      {showYearFilter && (
        <Box sx={{ width: '100%' }}>
          <FormControl sx={{ '.MuiSvgIcon-root': { color: 'rgba(68, 68, 68, 1)' } }} fullWidth>
            <Typography mb={'7px'}>Ano*</Typography>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              sx={{ color: '#000', height: '45px' }}
              value={tempSelectedYear || ''}
              onChange={handleYearChange}
            >
              <MenuItem value="">
                <em>Selecione um ano</em>
              </MenuItem>
              {listYears && listYears.length > 0 ? (
                listYears.map((item) => (
                  <MenuItem key={item} value={String(item)}>
                    {item}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="2024">2024</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      )}

      {showMonthFilter && (
        <DemoItem sx={{ width: '100%' }}>
          <Typography>Mês*</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={tempSelectedMonth !== null ? dayjs().month(tempSelectedMonth) : null}
              onChange={handleMonthChange}
              views={['month']}
              sx={{
                '& .MuiInputBase-root': {
                  height: '45px',
                },
              }}
            />
          </LocalizationProvider>
        </DemoItem>
      )}

      {showPeriodFilter && (
        <DemoItem sx={{ width: '100%' }}>
          <Typography>Período*</Typography>
          <FormControl fullWidth margin="normal" sx={{ height: '100%' }}>
            <Select
              labelId="period-select-label"
              value={selectedPeriod}
              onChange={handlePeriodChange}
              displayEmpty
              fullWidth
              sx={{ height: '45px' }}
            >
              <MenuItem value="">
                <em>Selecione um período</em>
              </MenuItem>
              {periodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DemoItem>
      )}

      <Button
        sx={{
          p: '0 5px',
          borderRadius: '24px',
          fontSize: '18px',
          height: '45px',
          color: '#fff !important',
          width: 'auto',
          minWidth: 120,
          fontWeight: 700,
          bottom: 0,
          background: '#15AB51',
          textTransform: 'capitalize',
        }}
        variant="contained"
        onClick={handleCustomDateChange}
      >
        Buscar
      </Button>
    </Box>
  );
};

export default PeriodFilterDash;